import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdAccountBalance, MdLocationCity, MdHome, MdPerson, MdKeyboardArrowRight, MdKeyboardArrowDown, MdSearch, MdLocationOn, MdClose } from 'react-icons/md';
import VerifiedBadge from '../components/VerifiedBadge';

function RepsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [officesByLevel, setOfficesByLevel] = useState({});
  const [userLocation, setUserLocation] = useState({});
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [expandedOffice, setExpandedOffice] = useState(null);
  const [representatives, setRepresentatives] = useState({});
  const [loadingReps, setLoadingReps] = useState({});
  const [showAllReps, setShowAllReps] = useState({});

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    loadOffices();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOffices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/offices/by-region`);
      setOfficesByLevel(response.data.offices_by_level || {});
      setUserLocation(response.data.user_location || {});
    } catch (error) {
      console.error('Failed to load offices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRepresentatives = async (officeId, showAll = false) => {
    try {
      setLoadingReps(prev => ({ ...prev, [officeId]: true }));
      const response = await axios.get(`${API_URL}/api/offices/${officeId}/representatives`, {
        params: { show_all: showAll, limit: 3 }
      });
      setRepresentatives(prev => ({ ...prev, [officeId]: response.data }));
      setShowAllReps(prev => ({ ...prev, [officeId]: showAll }));
    } catch (error) {
      console.error('Failed to load representatives:', error);
    } finally {
      setLoadingReps(prev => ({ ...prev, [officeId]: false }));
    }
  };

  const searchReps = async (query) => {
    if (query.length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    try {
      setSearching(true);
      const response = await axios.get(`${API_URL}/api/representatives/search`, { params: { q: query, limit: 8 } });
      setSearchResults(response.data.results || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchReps(value), 300);
  };

  const handleLevelClick = (level) => {
    setExpandedLevel(expandedLevel === level ? null : level);
    setExpandedOffice(null);
  };

  const handleOfficeClick = (officeId) => {
    if (expandedOffice === officeId) {
      setExpandedOffice(null);
    } else {
      setExpandedOffice(officeId);
      if (!representatives[officeId]) loadRepresentatives(officeId);
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'National': return <MdAccountBalance className="text-3xl text-white" />;
      case 'State': return <MdLocationCity className="text-3xl text-white" />;
      case 'Local': return <MdHome className="text-3xl text-white" />;
      default: return <MdAccountBalance className="text-3xl text-white" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'National': return 'from-blue-500 to-blue-700';
      case 'State': return 'from-purple-500 to-purple-700';
      case 'Local': return 'from-green-500 to-green-700';
      default: return 'from-gray-500 to-gray-700';
    }
  };

  const getLevelDescription = (level) => {
    switch (level) {
      case 'National': return 'Federal government positions including President, Congress, and Executive departments';
      case 'State': return `State-level offices for ${userLocation.state || 'your state'}`;
      case 'Local': return `Local government positions for ${userLocation.city || 'your city'} and ${userLocation.state || 'your state'}`;
      default: return '';
    }
  };

  const getMatchColor = (percentage) => {
    if (percentage >= 75) return 'text-green-600 bg-green-100';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-100';
    if (percentage >= 25) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const locationStr = [userLocation.city, userLocation.state, 'United States'].filter(Boolean).join(', ');

  return (
    <div className="max-w-4xl mx-auto pt-6" data-testid="reps-page">
      {/* Location Banner */}
      {userLocation.state && (
        <div className="mb-6 flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-primary/15 to-primary/5 border-l-4 border-primary rounded-lg shadow-sm" data-testid="location-banner">
          <MdLocationOn className="text-2xl text-primary flex-shrink-0" />
          <p className="text-base text-white font-rubik">
            You are viewing representatives for <span className="font-bold text-white">{locationStr}</span>
          </p>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6 relative" ref={searchRef}>
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            placeholder="Search for a representative by name..."
            className="w-full pl-10 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-primary shadow-sm"
            data-testid="rep-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setSearchResults([]); setShowDropdown(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <MdClose className="text-xl" />
            </button>
          )}
          {searching && (
            <div className="absolute right-10 top-1/2 -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            </div>
          )}
        </div>

        {/* Search Dropdown */}
        {showDropdown && searchResults.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-80 overflow-y-auto" data-testid="search-dropdown">
            {searchResults.map((rep) => (
              <button
                key={rep.profile_id}
                onClick={() => { setShowDropdown(false); setSearchQuery(''); navigate(`/representatives/${rep.profile_id}`); }}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                data-testid={`search-result-${rep.profile_id}`}
              >
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {rep.profile_picture_url ? (
                    <img src={rep.profile_picture_url} alt={rep.name} className="w-full h-full object-cover" />
                  ) : (
                    <MdPerson className="text-xl text-gray-400" />
                  )}
                </div>
                <div className="flex-grow min-w-0 text-left">
                  <p className="font-medium text-gray-900 truncate flex items-center gap-1">
                    {rep.name}
                    <VerifiedBadge status={rep.verification_status} />
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {rep.office} • {rep.office_level}
                    {rep.running_location && ` • ${rep.running_location}`}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
        {showDropdown && searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
          <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 p-4 text-center text-gray-500 text-sm">
            No representatives found
          </div>
        )}
      </div>

      {/* Offices Breakdown */}
      <div className="space-y-4">
        {['National', 'State', 'Local'].map((level) => {
          const offices = officesByLevel[level] || [];
          const totalReps = offices.reduce((sum, o) => sum + (o.representative_count || 0), 0);
          return (
            <div key={level} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => handleLevelClick(level)}
                className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid={`level-card-${level.toLowerCase()}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getLevelColor(level)} flex items-center justify-center`}>
                    {getLevelIcon(level)}
                  </div>
                  <div className="text-left">
                    <h2 className="text-xl font-semibold text-gray-900 font-rubik">{level} Offices</h2>
                    <p className="text-sm text-gray-500">{offices.length} offices • {totalReps} representative{totalReps !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <MdKeyboardArrowDown className={`text-2xl text-gray-400 transition-transform ${expandedLevel === level ? 'rotate-180' : ''}`} />
              </button>

              {expandedLevel === level && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  <p className="text-sm text-gray-600 mb-4">{getLevelDescription(level)}</p>
                  {offices.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No offices available for this level.</p>
                  ) : (
                    <div className="space-y-2">
                      {offices.map((office) => (
                        <div key={office.office_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <button
                            onClick={() => handleOfficeClick(office.office_id)}
                            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                            data-testid={`office-${office.office_id}`}
                          >
                            <div className="text-left">
                              <h3 className="font-medium text-gray-900">{office.position_title}</h3>
                              <p className="text-sm text-gray-500">{office.category} • {office.representative_count || 0} running</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {office.representative_count > 0 && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                                  {office.representative_count} candidate{office.representative_count !== 1 ? 's' : ''}
                                </span>
                              )}
                              <MdKeyboardArrowRight className={`text-xl text-gray-400 transition-transform ${expandedOffice === office.office_id ? 'rotate-90' : ''}`} />
                            </div>
                          </button>

                          {expandedOffice === office.office_id && (
                            <div className="border-t border-gray-100 p-4 bg-gray-50">
                              {loadingReps[office.office_id] ? (
                                <div className="flex justify-center py-4">
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                </div>
                              ) : representatives[office.office_id]?.representatives?.length > 0 ? (
                                <>
                                  <div className="space-y-3">
                                    {representatives[office.office_id].representatives.map((rep) => (
                                      <Link
                                        key={rep.profile_id}
                                        to={`/representatives/${rep.profile_id}`}
                                        className="flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all"
                                        data-testid={`rep-card-${rep.profile_id}`}
                                      >
                                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                          {rep.profile_picture_url ? (
                                            <img src={rep.profile_picture_url} alt={rep.name} className="w-full h-full object-cover" />
                                          ) : (
                                            <MdPerson className="text-2xl text-gray-400" />
                                          )}
                                        </div>
                                        <div className="flex-grow min-w-0">
                                          <h4 className="font-medium text-gray-900 truncate flex items-center gap-1">
                                            {rep.name}
                                            <VerifiedBadge status={rep.verification_status} />
                                          </h4>
                                          <p className="text-sm text-gray-500">{rep.matching_responses} of {rep.total_user_responses} responses match</p>
                                        </div>
                                        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getMatchColor(rep.match_percentage)}`}>
                                          {rep.match_percentage}% match
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                  {representatives[office.office_id].total > 3 && !showAllReps[office.office_id] && (
                                    <button
                                      onClick={() => loadRepresentatives(office.office_id, true)}
                                      className="mt-3 w-full py-2 text-sm text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                    >
                                      Show all {representatives[office.office_id].total} representatives
                                    </button>
                                  )}
                                </>
                              ) : (
                                <p className="text-gray-500 text-center py-4">No representatives have declared for this office yet.</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Did You Know + How Matches Work */}
      <div className="mt-8 space-y-4">
        <div className="p-5 bg-gradient-to-r from-purple-600 to-indigo-700 rounded-xl shadow-sm" data-testid="did-you-know-banner">
          <p className="text-xs uppercase tracking-wide text-white/70 mb-1 font-rubik">Did you know?</p>
          <p className="text-sm text-white/95">
            There are approximately <span className="font-bold text-white">500,000 to 520,000</span> elected offices in the United States.
          </p>
        </div>
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>How matches work:</strong> Your match percentage is calculated based on how many of your poll responses
            align with each representative&apos;s responses. The more polls you answer, the more accurate your match will be.
          </p>
        </div>
      </div>
    </div>
  );
}

export default RepsPage;
